/* eslint-disable max-lines */
import React, {
 useState, useEffect, useContext, useCallback
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Tabs, TabsList, TabsTrigger, TabsContent
} from 'shared/components/ui/tabs';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 ArrowLeft, Save, Loader2
} from 'lucide-react';
import {
 bexSchemaService
} from 'shared/api/services/BEX';
import {
 SchemaColumnDataModel
} from 'shared/api/services/BEXO/Types/Bexo.types';
import {
 Context
} from 'shared/context/GlobalState';
import {
    SchemaListItem, SchemaField, SchemaRelationship, SchemaCalculation, SchemaEditorTab,
} from '../types/schema.types';
import SchemaFieldsTab from './schema-editor/SchemaFieldsTab';
import SchemaRelationshipsTab from './schema-editor/SchemaRelationshipsTab';
import SchemaCalculationsTab from './schema-editor/SchemaCalculationsTab';
import SchemaSettingsTab from './schema-editor/SchemaSettingsTab';

interface SchemaEditorProps {
    schema: SchemaListItem | null;
    initialTableName?: string;
    initialColumns?: SchemaColumnDataModel[];
    initialConnectionId?: string;
    onSave: () => void;
    onCancel: () => void;
}

let fieldIdCounter = 0;
function generateFieldId(): string {
    fieldIdCounter += 1;
    return `field_${Date.now()}_${fieldIdCounter}`;
}

function columnToField(col: SchemaColumnDataModel): SchemaField {
    return {
        ...col,
        id: generateFieldId(),
        isNew: false,
        isModified: false,
        previousColumnName: col.columnName,
    };
}

function fieldToColumn(field: SchemaField): SchemaColumnDataModel {
    return {
        columnName: field.columnName,
        dataType: field.dataType,
        length: field.length,
        fieldsAccessBy: field.fieldsAccessBy,
        description: field.description,
        readOnly: field.readOnly,
        visibleStatus: field.visibleStatus,
        format: field.format,
        default: field.default,
        constraints: field.constraints,
        references: field.references,
        validation: field.validation,
    };
}

/** Maps a field to the BEX server's expected column format */
function fieldToBexColumn(field: SchemaField) {
    return {
        columnName: field.columnName,
        dataType: field.dataType,
        constraints: field.constraints || [],
        description: field.description || '',
        ...(field.default ? { default: field.default } : {}),
        ...(field.validation ? {
            validation: Array.isArray(field.validation)
                ? field.validation.flat().join(',')
                : String(field.validation)
        } : {}),
        ...(field.references && typeof field.references === 'object' && field.references.table
            ? { references: field.references }
            : {}),
    };
}

const SchemaEditor: React.FC<SchemaEditorProps> = ({
    schema, initialTableName, initialColumns, initialConnectionId, onSave, onCancel,
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const {
 addNewNotifcation
} = useContext(Context);

    const [tableName, setTableName] = useState(schema?.tableName || initialTableName || '');
    const [entityName, setEntityName] = useState(schema?.tableName || initialTableName || '');
    const [connectionId, setConnectionId] = useState(schema?.connectionId || initialConnectionId || '');
    const [fields, setFields] = useState<SchemaField[]>([]);
    const [relationships, setRelationships] = useState<SchemaRelationship[]>([]);
    const [calculations, setCalculations] = useState<SchemaCalculation[]>([]);
    const [activeTab, setActiveTab] = useState<SchemaEditorTab>('fields');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [allSchemaNames, setAllSchemaNames] = useState<string[]>([]);

    const isEditing = !!schema;

    // Load schema data
    useEffect(() => {
        if (schema) {
            // Editing existing schema - load its columns
            const loadSchema = async () => {
                if (!appId || !oragID) return;
                setIsLoading(true);
                try {
                    const res = await bexSchemaService.retrieveSchemas(schema.tableName, appId, oragID, schema.connectionId || undefined);
                    const rawSchemas = res.data?.schemas || res.data || [];
                    let columns: SchemaColumnDataModel[] = [];

                    if (Array.isArray(rawSchemas)) {
                        rawSchemas.forEach((item: any) => {
                            if (Array.isArray(item.columns)) {
                                columns = item.columns;
                            } else if (item.columnName) {
                                columns.push(item);
                            }
                        });
                    }

                    const loadedFields = columns.map(columnToField);
                    setFields(loadedFields);

                    // Extract relationships from columns with FOREIGN KEY
                    const rels: SchemaRelationship[] = columns
                        .filter((c) => c.constraints?.includes('FOREIGN KEY') && c.references?.table)
                        .map((c) => ({
                            id: generateFieldId(),
                            name: c.columnName,
                            targetSchema: c.references.table,
                            type: c.references.type === 'one-to-one' ? '1:1' as const :
                                  c.references.type === 'one-to-many' ? '1:N' as const : 'N:1' as const,
                            sourceField: c.columnName,
                            targetField: c.references.column,
                        }));
                    setRelationships(rels);

                    // Extract calculations from equation-type columns
                    const calcs: SchemaCalculation[] = columns
                        .filter((c) => c.dataType === 'equation')
                        .map((c) => ({
                            id: generateFieldId(),
                            fieldName: c.columnName,
                            formula: c.default || '',
                            dependencies: [],
                            returnType: 'string',
                        }));
                    setCalculations(calcs);
                } catch (error) {
                    console.error('Error loading schema:', error);
                } finally {
                    setIsLoading(false);
                }
            };
            loadSchema();
        } else if (initialColumns) {
            // Prefilled from AI/Import
            setFields(initialColumns.map(columnToField));
        }
    }, [schema, initialColumns, appId, oragID]);

    // Load all schema names for relationship target selection
    useEffect(() => {
        const loadSchemaNames = async () => {
            if (!appId || !oragID) return;
            try {
                const res = await bexSchemaService.retrieveSchemas('*', appId, oragID, connectionId || undefined);
                const rawSchemas = res.data?.schemas || res.data || [];
                if (Array.isArray(rawSchemas)) {
                    const names = new Set<string>();
                    rawSchemas.forEach((item: any) => {
                        const name = item.tableName || item.table_name;
                        if (name) names.add(name);
                    });
                    setAllSchemaNames(Array.from(names));
                }
            } catch (error) {
                console.error('Error loading schema names:', error);
            }
        };
        loadSchemaNames();
    }, [appId, oragID]);

    const handleSave = useCallback(async () => {
        if (!oragID || !appId) return;
        if (!tableName.trim()) {
            addNewNotifcation('Please enter a schema name', 'danger');
            return;
        }
        if (fields.length === 0) {
            addNewNotifcation('Please add at least one field', 'danger');
            return;
        }

        setIsSaving(true);
        try {
            if (isEditing) {
                const newFields = fields.filter((f) => f.isNew);
                const modifiedFields = fields.filter((f) => f.isModified && !f.isNew);

                const modifications: any = { columns: {} };
                if (newFields.length > 0) {
                    modifications.columns.add = newFields.map(fieldToBexColumn);
                }
                if (modifiedFields.length > 0) {
                    modifications.columns.modify = modifiedFields.map((f) => ({
                        ...fieldToBexColumn(f),
                        originalColumnName: f.previousColumnName || f.columnName,
                    }));
                }
                if (modifications.columns.add || modifications.columns.modify) {
                    const modifyPayload = {
                        schemas: [{ tableName, modifications }],
                    };
                    await bexSchemaService.modifySchemas(modifyPayload, appId, oragID, connectionId || undefined);
                }

                addNewNotifcation('Schema updated successfully', 'success');
            } else {
                // Creating new schema
                const payload = {
                    schemas: [{
                        tableName,
                        columns: fields.map(fieldToBexColumn),
                    }],
                };
                await bexSchemaService.createSchemas(oragID, appId, payload, connectionId || undefined);
                addNewNotifcation('Schema created successfully', 'success');
            }

            onSave();
        } catch (error: any) {
            console.error('Save failed:', error);
            addNewNotifcation(error.message || 'Failed to save schema', 'danger');
        } finally {
            setIsSaving(false);
        }
    }, [oragID, appId, tableName, fields, isEditing, connectionId, addNewNotifcation, onSave]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 w-full py-4">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={onCancel} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Schemas
                </Button>
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <h2 className="text-2xl font-bold">
                            {isEditing ? 'Edit Schema' : 'Create New Schema'}
                        </h2>
                        {isEditing ? (
                            <p className="text-muted-foreground mt-1">
                                Editing <span className="font-medium">{tableName}</span>
                            </p>
                        ) : (
                            <div className="mt-3 max-w-md">
                                <Label htmlFor="tableName">Schema Name</Label>
                                <Input
                                    id="tableName"
                                    placeholder="e.g., users, products, orders"
                                    value={tableName}
                                    onChange={(e) => {
                                        setTableName(e.target.value);
                                        if (!entityName || entityName === tableName) {
                                            setEntityName(e.target.value);
                                        }
                                    }}
                                    className="mt-1"
                                />
                            </div>
                        )}
                    </div>
                    <Button onClick={handleSave} disabled={isSaving || !tableName.trim()}>
                        {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        {isSaving ? 'Saving...' : 'Save Schema'}
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SchemaEditorTab)}>
                <TabsList>
                    <TabsTrigger value="fields">
                        Fields ({fields.filter((f) => f.dataType !== 'equation').length})
                    </TabsTrigger>
                    <TabsTrigger value="relationships">
                        Relationships ({relationships.length})
                    </TabsTrigger>
                    <TabsTrigger value="calculations">
                        Calculations ({calculations.length})
                    </TabsTrigger>
                    <TabsTrigger value="settings">
                        Settings
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="fields" className="mt-4">
                    <SchemaFieldsTab
                        fields={fields.filter((f) => f.dataType !== 'equation')}
                        setFields={setFields}
                        isNewSchema={!isEditing}
                    />
                </TabsContent>

                <TabsContent value="relationships" className="mt-4">
                    <SchemaRelationshipsTab
                        relationships={relationships}
                        setRelationships={setRelationships}
                        currentSchemaName={tableName}
                        fields={fields}
                        allSchemaNames={allSchemaNames}
                    />
                </TabsContent>

                <TabsContent value="calculations" className="mt-4">
                    <SchemaCalculationsTab
                        calculations={calculations}
                        setCalculations={setCalculations}
                        fields={fields}
                        setFields={setFields}
                    />
                </TabsContent>

                <TabsContent value="settings" className="mt-4">
                    <SchemaSettingsTab
                        entityName={entityName}
                        setEntityName={setEntityName}
                        connectionId={connectionId}
                        setConnectionId={setConnectionId}
                        tableName={tableName}
                        isNewSchema={!isEditing}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default SchemaEditor;
